<script setup lang="ts">
import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth.store'
import { useOrganisationStore } from '@/stores/organisation.store'
import ProfilePictureComp from '@/components/header/auth/profile/ProfilePictureComp.vue'
import SwitchingAccountLoader from '@/components/organisation/shared/SwitchingAccountLoader.vue'
import Logo from '@/components/utils/LogoComp.vue'
const props = defineProps({
  isBlogPage: {
    type: Boolean,
    required: false,
    default: false
  },
  isOnboardingFlow: {
    type: Boolean,
    required: false,
    default: false
  }
})

const organisationStore = useOrganisationStore()
const authStore = useAuthStore()

const previousOrgName = computed(() => {
  return organisationStore.previousOrgName
})
const currentOrgName = computed(() => {
  return organisationStore.currentOrgName
})

const personalAccount = computed(() => {
  if (authStore?.firstName == currentOrgName?.value) {
    return true
  } else {
    return false
  }
})

const previousPersonalAccount = computed(() => {
  if (authStore?.firstName == previousOrgName?.value) {
    return true
  } else {
    return false
  }
})
</script>

<template>
  <div
    v-if="!props.isOnboardingFlow"
    class="section-new-white fixed left-0 top-0 z-[60] h-screen w-full overflow-y-auto overflow-x-hidden"
  >
    <div class="h-full min-h-[100vh] w-full">
      <div class="flex flex-col">
        <div
          class="flex items-center justify-between border-b border-neutral-300 px-4 py-2 md:px-36 dark:border-neutral-800"
        >
          <!-- logo with link to home page -->
          <Logo :is-link="false" />
        </div>
      </div>

      <div class="text-md mt-[25%] flex h-full w-full justify-center font-medium">
        {{ props.isBlogPage ? 'Loading...' : 'Loading Account...' }}
      </div>
    </div>
  </div>
  <div
    v-else
    class="section-new-white fixed left-0 top-0 z-[60] h-screen w-full overflow-y-auto overflow-x-hidden"
  >
    <div
      class="flex h-full min-h-[100vh] w-full flex-col items-center justify-center bg-[#fff] backdrop-blur-md"
    >
      <div class="flex w-full flex-col">
        <div
          class="flex items-center justify-between border-b border-neutral-300 px-4 py-2 md:px-36 dark:border-neutral-800"
        >
          <!-- logo with link to home page -->
          <Logo :is-link="false" />
        </div>
      </div>
      <div
        v-if="previousOrgName && currentOrgName && previousOrgName !== currentOrgName"
        class="flex h-full flex-col items-center justify-between p-5 sm:min-w-[800px]"
      >
        <div class="flex h-[70%] flex-col items-center justify-center gap-2">
          <div class="flex w-full flex-row items-start justify-between gap-2">
            <div class="flex w-[33%] flex-col items-center justify-center">
              <div
                class="flex flex-col items-center justify-center"
                v-if="!previousPersonalAccount"
              >
                <ProfilePictureComp
                  :current-name="previousOrgName"
                  :widthAndHeightClass="'w-20 h-20'"
                  :initialFontSize="'text-3xl'"
                />
                <h1 class="text-center text-2xl font-semibold leading-[47px] text-sky-950">
                  {{ previousOrgName }}
                </h1>
              </div>
              <div v-else class="flex flex-col items-center justify-center">
                <ProfilePictureComp
                  :showPersonalProfilePic="authStore.profilePictureUrl != null"
                  :isUserProfileImage="true"
                  :widthAndHeightClass="'w-20 h-20'"
                  :initialFontSize="'text-3xl'"
                  :current-name="previousOrgName"
                />
                <h1 class="text-center text-2xl font-semibold leading-[47px] text-sky-950">
                  {{ previousOrgName }}
                </h1>
              </div>
            </div>
            <div class="relative flex h-5 w-[33%] items-center justify-center pt-10">
              <SwitchingAccountLoader />
            </div>
            <div class="flew-wrap flex w-[33%] flex-col items-center justify-center">
              <div class="flex flex-col items-center justify-center" v-if="!personalAccount">
                <ProfilePictureComp
                  :current-name="currentOrgName"
                  :widthAndHeightClass="'w-20 h-20'"
                  :initialFontSize="'text-3xl'"
                />
                <h1 class="text-center text-2xl font-semibold leading-[47px] text-sky-950">
                  {{ currentOrgName }}
                </h1>
              </div>
              <div v-else class="flex flex-col items-center justify-center">
                <ProfilePictureComp
                  :showPersonalProfilePic="authStore.profilePictureUrl != null"
                  :isUserProfileImage="true"
                  :widthAndHeightClass="'w-20 h-20'"
                  :initialFontSize="'text-3xl'"
                  :current-name="currentOrgName"
                />
                <h1 class="text-center text-2xl font-semibold leading-[47px] text-sky-950">
                  {{ currentOrgName }}
                </h1>
              </div>
            </div>
          </div>
          <div class="text-center text-lg font-semibold leading-[47px] text-sky-950">
            We are switching between your accounts. Your coding journey is about to begin..
          </div>
        </div>
        <!-- <div class="relative h-[20%]">
          <div class="relative h-3 w-[510px] rounded-[10px] bg-slate-300">
            <div class="absolute left-0 top-0 h-full w-10 rounded-[10px] bg-slate-700"></div>
          </div>
        </div> -->
      </div>
      <div v-else class="flex h-full flex-col items-center justify-between p-5 sm:min-w-[800px]">
        <div
          v-if="!personalAccount"
          class="flex h-[70%] flex-col items-center justify-center gap-2"
        >
          <div class="flex flex-col items-center justify-center">
            <ProfilePictureComp
              :current-name="currentOrgName"
              :showPersonalProfilePic="authStore.profilePictureUrl != null"
              :isUserProfileImage="true"
              :widthAndHeightClass="'w-20 h-20'"
              :initialFontSize="'text-3xl'"
            />
            <h1 class="text-center text-2xl font-semibold leading-[47px] text-sky-950">
              {{ currentOrgName }}
            </h1>
          </div>

          <div class="text-center text-lg font-semibold leading-[47px] text-sky-950">
            Loading your environment
          </div>
        </div>
        <div v-else class="flex h-[70%] flex-col items-center justify-center gap-2">
          <div class="flex flex-col items-center justify-center">
            <ProfilePictureComp
              :showPersonalProfilePic="authStore.profilePictureUrl != null"
              :isUserProfileImage="true"
              :widthAndHeightClass="'w-20 h-20'"
              :initialFontSize="'text-3xl'"
              :current-name="currentOrgName"
            />
            <h1 class="text-center text-2xl font-semibold leading-[47px] text-sky-950">
              {{ currentOrgName }}
            </h1>
          </div>

          <div class="text-center text-lg font-semibold leading-[47px] text-sky-950">
            Loading your environment
          </div>
        </div>
        <div class="relative h-[20%]">
          <SwitchingAccountLoader />
        </div>
      </div>
    </div>
  </div>
</template>
